import useSWR from 'swr'

import { FetchError } from '../../../../../data/fetchError'
import { FetchResponse } from '../../../../../data/fetchResponse'
import { FetchResult } from '../../../../../data/fetchResult'
import { fetchJson } from '../../../../../data/fetchers'
import {
  CustomFieldDefinition,
  CustomFieldType,
  MasterDataSupplier,
  SupplierCompanyCode,
  SupplierCustomField,
} from './model'

// Master data custom field definitions
export function useAllCustomFieldDefinitions(
  type?: CustomFieldType,
): FetchResult<CustomFieldDefinition[]> {
  // Build query parameters
  const params = new URLSearchParams()
  if (type) {
    params.append('type', type.toString())
  }
  const queryString = params.toString()
  const url = `/masterdata/customfielddefinitions${
    queryString ? `?${queryString}` : ''
  }`

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<CustomFieldDefinition[]>,
    FetchError
  >(url, fetchJson<CustomFieldDefinition[]>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// CustomFieldDefinitionResponse
export function useCustomFieldDefinitionById(
  id?: string,
  type?: CustomFieldType,
): FetchResult<CustomFieldDefinition> {
  // Build query parameters
  const params = new URLSearchParams()
  if (type) {
    params.append('type', type.toString())
  }
  const queryString = params.toString()

  let url = id ? `/masterdata/customfielddefinitions/${id}` : null
  url = url && queryString ? `${url}?${queryString}` : url

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<CustomFieldDefinition>,
    FetchError
  >(url, fetchJson<CustomFieldDefinition>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// Master data supplier company codes
// TODO: CHECK TYPE gibt keine supplierId oder createdAt
export function useAllSupplierCompanyCodes(
  supplierId?: string,
  companyCodeId?: string,
  includeSupplierDetails = true,
  includeCompanyCodeDetails = true,
): FetchResult<SupplierCompanyCode[]> {
  // Build query parameters
  const params = new URLSearchParams()
  if (supplierId) {
    params.append('supplierId', supplierId)
  }
  if (companyCodeId) {
    params.append('companyCodeId', companyCodeId)
  }
  if (includeSupplierDetails) {
    params.append('includeSupplierDetails', includeSupplierDetails.toString())
  }
  if (includeCompanyCodeDetails) {
    params.append(
      'includeCompanyCodeDetails',
      includeCompanyCodeDetails.toString(),
    )
  }

  const queryString = params.toString()
  const url = `/masterdata/suppliercompanycodes${
    queryString ? `?${queryString}` : ''
  }`

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<SupplierCompanyCode[]>,
    FetchError
  >(url, fetchJson<SupplierCompanyCode[]>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// Master data suppliers
export function useAllMasterDataSuppliers(
  includeCustomFields = false,
  includeCompanyCodes = false,
): FetchResult<MasterDataSupplier[]> {
  // Build query parameters
  const params = new URLSearchParams()
  if (includeCustomFields) {
    params.append('includeCustomFields', includeCustomFields.toString())
  }
  if (includeCompanyCodes) {
    params.append('includeCompanyCodes', includeCompanyCodes.toString())
  }

  const queryString = params.toString()
  const url = `/masterdata/suppliers${queryString ? `?${queryString}` : ''}`

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<MasterDataSupplier[]>,
    FetchError
  >(url, fetchJson<MasterDataSupplier[]>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useSupplierById(
  id?: string | null,
  includeCustomFields = false,
  includeCompanyCodes = false,
): FetchResult<MasterDataSupplier> {
  // Build query parameters
  const params = new URLSearchParams()
  if (includeCustomFields) {
    params.append('includeCustomFields', includeCustomFields.toString())
  }
  if (includeCompanyCodes) {
    params.append('includeCompanyCodes', includeCompanyCodes.toString())
  }

  const queryString = params.toString()

  let url = id ? `/masterdata/suppliers/${id}` : null
  url = url && queryString ? `${url}?${queryString}` : url

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<MasterDataSupplier>,
    FetchError
  >(url, fetchJson<MasterDataSupplier>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// Master data suppliers custom fields
export function useAllSupplierCustomFields(
  supplierId: string,
): FetchResult<SupplierCustomField[]> {
  const url = supplierId
    ? `/masterdata/suppliers/${supplierId}/customfields`
    : null

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<SupplierCustomField[]>,
    FetchError
  >(url, fetchJson<SupplierCustomField[]>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useSupplierCustomFieldById(
  supplierId?: string,
  customFieldId?: string,
): FetchResult<SupplierCustomField> {
  const url =
    supplierId && customFieldId
      ? `/masterdata/suppliers/${supplierId}/customfields/${customFieldId}`
      : null
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<SupplierCustomField>,
    FetchError
  >(url, fetchJson<SupplierCustomField>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}
